import type { Contact } from './types';

/*
 * ⚠️ READING A CONTACT'S LEAD SOURCE — TWO KEYS, AND ONLY ONE OF THEM IS SAFE
 * TO WRITE BACK.
 *
 * ## What is actually on the wire
 *
 *   leadSource     string | null            the legacy free text — WRITABLE
 *   leadSourceRef  { id, name } | null      the resolved picker row — READ-ONLY
 *
 * The write key for the picker is `leadSourceId`, a bare number. There is no
 * `leadSourceText` key on any response — do not read one.
 *
 * ## The incident this file exists for, and why its shape still governs
 *
 * `contact.leadSource` was briefly served as the resolved OBJECT rather than
 * the free text. `ContactFormModal` read `contact.leadSource ?? ''` into a
 * STRING form value, rendered it in a text input, and **writes that field
 * back** — so the object stringified to the literal `[object Object]`, and the
 * next save of an unrelated field (a phone number) wrote it over the stored
 * lead source. Silent, unrecoverable loss on the most ordinary action on that
 * screen.
 *
 * Worth naming why that shape is the worst available: a THROW is loud and a
 * BLANK is visible, but a plausible-looking wrong string is neither.
 * `[object Object]` at least announces itself to a human; the same accident
 * carrying a real-looking word would not have.
 *
 * ## Which is why there are TWO functions here, and they must not be merged
 *
 * `leadSourceLabel` is for DISPLAY and prefers `leadSourceRef.name`.
 * `leadSourceInputValue` feeds the WRITABLE free-text control and returns
 * `leadSource` and nothing else.
 *
 * ⚠️ **A single helper serving both is the original incident in slow motion.**
 * The two keys legitimately differ — `leadSource` holds what somebody typed
 * (`'Conference — NAFSA 2026'`) while the row names the category
 * (`'Conference'`) — so feeding the resolved label into a control bound to
 * `leadSource` saves the category OVER the typed text. Not a crash and not a
 * blank: a narrower, entirely plausible string, written silently, and the
 * detail gone. Exactly the failure above with the loud tell filed off.
 *
 * So: never widen `leadSourceInputValue` to fall back to the ref, however
 * obviously "more complete" that looks at the call site.
 */

/**
 * What a person should read for this contact's lead source, under either wire
 * shape, or `null` when there is nothing recorded.
 *
 * NEVER interpolate `contact.leadSource` directly. It is a union, and the
 * object branch stringifies to `[object Object]` without any type error —
 * template literals accept `unknown`, so `tsc` cannot catch it for you.
 */
export function leadSourceLabel(contact: Contact): string | null {
  /*
   * The resolved row first: it is what the picker writes and what the record
   * now means; the text is legacy.
   *
   * ⚠️ That is a real loss of detail, accepted knowingly rather than
   * overlooked — `NAFSA 2026` disappears from the screen while it is still in
   * the column. When this surface moves to `leadSourceId` properly, the text
   * should be offered BESIDE the picker rather than replaced by it. Do not
   * "fix" the order to prefer stale text over the live row.
   */
  const name = contact.leadSourceRef?.name.trim() ?? '';
  if (name !== '') return name;

  /*
   * A resolved row with a blank name is not "no lead source" — the row exists.
   * Falling through to the text degrades a half-configured master to what
   * somebody typed rather than to silence.
   *
   * ⚠️ Tested POSITIVELY for `string`, never `!== null`. The wire says this key
   * is `string | null`, but if an object ever reappeared here a negative test
   * would interpolate it as `[object Object]` — the original incident — while a
   * positive one renders nothing. **A missing value is visible; a plausible
   * wrong one is not**, so the unknown case fails towards blank on purpose.
   */
  const text = typeof contact.leadSource === 'string' ? contact.leadSource.trim() : '';
  return text !== '' ? text : null;
}

/**
 * The value a FREE-TEXT lead source input should hold for this contact.
 *
 * ⚠️ **This is deliberately NOT `leadSourceLabel(contact) ?? ''`.** That
 * control is bound to `leadSource` and writes it back, so
 * whatever this returns is a value the form will SAVE. Returning the resolved
 * `leadSourceRef.name` would therefore write the picker's category over the
 * typed text on the next save of any unrelated field — the same silent
 * overwrite as the `[object Object]` incident, with a plausible-looking string
 * in place of the obvious one.
 *
 * So it reads the writable key and only the writable key. `''` when nothing is
 * recorded, never the word "null", never a derived label.
 *
 * A contact whose source was set through the picker therefore shows an EMPTY
 * text box while {@link leadSourceLabel} shows the row's name elsewhere on the
 * screen. That reads oddly and is still correct: the box holds the deprecated
 * free text, which genuinely is empty. It resolves when this form moves to
 * `leadSourceId`, not by papering over it here.
 */
export function leadSourceInputValue(contact: Contact): string {
  return typeof contact.leadSource === 'string' ? contact.leadSource.trim() : '';
}
